/**
 * Base API Client
 *
 * Configured axios instance with interceptors for:
 * - Authentication token injection
 * - Error handling
 * - Request/response logging
 */

import axios from 'axios';
import { config } from '../config/env';

// Create axios instance
const api = axios.create({
  baseURL: config.apiBaseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - Add auth token and session ID
api.interceptors.request.use(
  (reqConfig) => {
    // Get token from localStorage (updated for backend integration)
    const token = localStorage.getItem('authToken');
    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }

    // Add session ID only for cart operations (guest users)
    const isCartRequest = reqConfig.url?.includes('/cart');
    if (isCartRequest) {
      const sessionId = localStorage.getItem('cartSessionId');
      if (sessionId) {
        reqConfig.headers['X-Session-Id'] = sessionId;
      }
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${reqConfig.method.toUpperCase()} ${reqConfig.url}`, {
        params: reqConfig.params,
        data: reqConfig.data
      });
    }

    return reqConfig;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle session ID and errors
api.interceptors.response.use(
  (response) => {
    // Store session ID if returned from cart operations (for guest cart)
    const isCartRequest = response.config.url?.includes('/cart');

    if (isCartRequest) {
      // Check for session ID in response data
      if (response.data?.sessionId) {
        localStorage.setItem('cartSessionId', response.data.sessionId);
        if (process.env.NODE_ENV === 'development') {
          console.log('[API] Session ID updated from response');
        }
      }

      // Also check for session ID in response headers (X-Session-Id)
      const headerSessionId = response.headers['x-session-id'] || response.headers['X-Session-Id'];
      if (headerSessionId) {
        localStorage.setItem('cartSessionId', headerSessionId);
        if (process.env.NODE_ENV === 'development') {
          console.log('[API] Session ID updated from header');
        }
      }

      // Check if cart merge was attempted by backend
      // When user logs in with a guest cart, backend auto-merges and sets this header
      // Clear session ID after merge - it's no longer needed
      const mergeAttempted = response.headers['x-cart-merge-attempted'];
      if (mergeAttempted === 'true') {
        localStorage.removeItem('cartSessionId');
        if (process.env.NODE_ENV === 'development') {
          console.log('[API] Cart merge completed, session ID cleared');
        }
      }
    }

    return response;
  },
  (error) => {
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      console.error(`[API] Error ${status}:`, data);

      // Handle specific status codes
      switch (status) {
        case 401: {
          // Unauthorized - but handle different scenarios
          const requestUrl = error.config?.url || '';
          const isLoginRequest = requestUrl.includes('/auth/login');
          const isRegisterRequest = requestUrl.includes('/auth/register');
          const isAuthMeRequest = requestUrl.includes('/auth/me');

          // Don't clear token for failed login/register attempts (expected 401s)
          if (isLoginRequest || isRegisterRequest) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[API] Login/register attempt failed');
            }
            break;
          }

          // Only clear token if /auth/me specifically fails
          // This means the token is actually invalid
          if (isAuthMeRequest) {
            console.warn('[API] Session expired');
            localStorage.removeItem('authToken');

            // Only redirect if not already on login page
            if (window.location.pathname !== '/login') {
              window.location.href = '/login?reason=session_expired';
            }
            break;
          }

          // For other 401s, don't auto-clear token
          // Let the calling code decide what to do
          break;
        }

        case 403:
          // Forbidden
          console.error('[API] Access forbidden');
          break;

        case 404:
          // Not found
          console.error('[API] Resource not found');
          break;

        case 500:
          // Server error
          console.error('[API] Server error');
          break;

        default:
          console.error('[API] Unknown error:', status);
      }

      // Return backend error format (ApiResponse structure)
      // Extract validation errors if available in data object
      let errorMessage = data.message || 'An error occurred';

      // Check if data.data contains field-specific validation errors
      if (data.data && typeof data.data === 'object') {
        const validationErrors = [];

        // eslint-disable-next-line no-unused-vars
        for (const [_, message] of Object.entries(data.data)) {
          if (typeof message === 'string') {
            validationErrors.push(message);
          } else if (Array.isArray(message)) {
            validationErrors.push(...message);
          }
        }

        if (validationErrors.length > 0) {
          errorMessage = validationErrors.join('. ');
        }
      }

      return Promise.reject({
        status,
        message: errorMessage,
        data: data.data || null,
        timestamp: data.timestamp
      });
    } else if (error.request) {
      // Request made but no response received
      console.error('[API] No response received:', error.request);
      return Promise.reject({
        status: 0,
        message: 'No response from server',
        code: 'NO_RESPONSE',
        details: null
      });
    } else {
      // Error in request setup
      console.error('[API] Request setup error:', error.message);
      return Promise.reject({
        status: 0,
        message: error.message,
        code: 'REQUEST_ERROR',
        details: null
      });
    }
  }
);

export default api;
